import axios from 'axios';
import config from '../config';

class LintasAPIService {
  // constructors
  constructor() {
    this.baseurl = config.API_URL;
    this.accessToken = localStorage.getItem('token');
  }

  initAxios() {
    this.axiosError = null;
    this.apiData = null;
  }

  static createHeader = (isFileUpload = false) => {
    const Token = localStorage.getItem('token');
    const headers = {
      Accept: 'application/json',
      'Content-Type': !isFileUpload
        ? 'application/json'
        : 'multipart/form-data',
    };
    if (Token && Token !== null) {
      headers.Authorization = `Bearer ${Token}`;
    }
    return headers;
  };

  axiosCall = ({
    method, url, data, headers, timeout, responseType,
  }) => {
    axios.defaults.baseURL = this.baseurl;
    axios.defaults.timeout = timeout || 400000;
    const requestParams = {
      url,
      method: method.toLowerCase(),
      headers: { ...LintasAPIService.createHeader(), ...headers },
      data,
    };
    if (responseType) {
      requestParams.responseType = responseType;
    }
    return axios(requestParams);
  };

  // server status check
  async checkServerStatus() {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: '/server-status',
    });
  }

  // dash user login
  async dashUserLogin(username, password) {
    // init tokens
    this.accessToken = null;
    this.initAxios();
    return this.axiosCall({
      method: 'POST',
      url: '/login/dashUser',
      data: {
        username,
        password,
      },
    });
  }

  // get all sites
  async getAllSites({ page, primaryQuery, searchQuery }) {
    const url = {
      key: `/sites?page=${page}`,
    };
    if (primaryQuery) {
      const { key } = url;
      const nurl = `${key}&${primaryQuery}`;
      url.key = nurl;
    }
    if (searchQuery) {
      const { key } = url;
      const nurl = `${key}&${searchQuery}`;
      url.key = nurl;
    }
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: url.key,
    });
  }

  async getAllSitesFiter({
    page, primaryQuery, searchQuery, status,
  }) {
    const url = {
      key: `/sites?page=${page}`,
    };
    if (primaryQuery) {
      const { key } = url;
      const nurl = `${key}&${primaryQuery}`;
      url.key = nurl;
    }
    if (searchQuery) {
      const { key } = url;
      const nurl = `${key}&${searchQuery}`;
      url.key = nurl;
    }
    url.key = `${url.key}&auditorAction=${status}`;
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: url.key,
    });
  }

  // get all suppliers
  async getSuppliers() {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: '/vendors',
    });
  }

  // create supplier (vendor) user
  async createSupplierUser({ supplierId, data }) {
    this.initAxios();
    return this.axiosCall({
      method: 'POST',
      url: `/vendor-users/${supplierId}`,
      data,
    });
  }

  // get supplier (vendor) users
  async getSupplierUsers({ supplierId, page }) {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: `/vendor-users?vendorId=${supplierId}&page=${page}`,
    });
  }

  // create dash user
  async createDashUser({ data }) {
    this.initAxios();
    return this.axiosCall({
      method: 'POST',
      url: '/dash-users',
      data,
    });
  }

  // get dash users
  async getDashUsers({ page, userType, dependedValue }) {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: `/dash-users?userType=${userType}&page=${page}${
        dependedValue ? `&dependedValue=${dependedValue}` : ''
      }`,
    });
  }

  // get dash users
  async getAllDashUsers() {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: '/get-dashUsers',
    });
  }

  // get primary filters
  async getPrimaryFilters() {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: '/sites/primaryFilters',
    });
  }

  // assign product to dash user
  async assignProduct(data) {
    this.initAxios();
    return this.axiosCall({
      method: 'POST',
      url: '/dash-users/assign-brands',
      data,
    });
  }

  // get estimates
  async getEstimates({ companyCode, clientId, productId }) {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: `/getEstimates?companyCode=${companyCode}&clientId=${clientId}&productId=${productId}`,
    });
  }

  // get all supplier users
  async getVendorUsers(supplierId) {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: `/get-vendor-users?supplierId=${supplierId}`,
    });
  }

  // assing vendor user to site
  async assignVendorToSite(data) {
    this.initAxios();
    return this.axiosCall({
      method: 'POST',
      url: '/sites/assign-vendor',
      data,
    });
  }

  // assing frequency to sites
  async assignFrequencyToSites(data) {
    this.initAxios();
    return this.axiosCall({
      method: 'POST',
      url: '/sites/assign-frequency',
      data,
    });
  }

  // get filters
  async getFilters() {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: '/sites/filters',
    });
  }

  // get site reports by site id
  async getSiteReportsBySiteId(siteId) {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: `/get-sitereports?siteId=${siteId}`,
    });
  }

  // approve reject sites
  async approveRejectSiteReport(data, siteReportId) {
    this.initAxios();
    return this.axiosCall({
      method: 'PUT',
      url: `/sites/${siteReportId}`,
      data,
    });
  }

  // reset password
  async resetPassword(data, userId) {
    this.initAxios();
    return this.axiosCall({
      method: 'PUT',
      url: `/reset-password/${userId}`,
      data,
    });
  }

  // get companies
  async getCompanies() {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: '/getCompanies',
    });
  }

  // get getClientsByCompanyCode
  async getClientsByCompanyCode(companyCode) {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: `/getClientsByCompanyCode?companyCode=${companyCode}`,
    });
  }

  // getProductsByClientId
  async getProductsByClientId(clientId) {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: `/getProductsByClientId?clientId=${clientId}`,
    });
  }

  // getSitesDataByEstimateId
  async getSitesDataByEstimateId(estimateId) {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: `/ROMSData/getSitesDataByEstimateId?estimateId=${estimateId}`,
    });
  }

  // dashboard Reports
  async dashboardReports(filter) {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: `/dashboardReports?filter=${filter}`,
    });
  }

  // removeAssingedProduct
  async removeAssingedProduct(userId, productId) {
    this.initAxios();
    return this.axiosCall({
      method: 'DELETE',
      url: `/remove-assigned-product?userId=${userId}&productId=${productId}`,
    });
  }

  // update vendor user
  async updateVendorUser(data, id) {
    this.initAxios();
    return this.axiosCall({
      method: 'PUT',
      url: `/vendors/${id}`,
      data,
    });
  }

  // update dash user
  async updateDashUser(data, id) {
    this.initAxios();
    return this.axiosCall({
      method: 'PUT',
      url: `/dash-users/${id}`,
      data,
    });
  }

  // get product users
  async getProductUsers(productId) {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: `/get-product-users?productId=${productId}`,
    });
  }

  // download vendor users
  async exportVendorUsers(supplierId) {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: `/export-vendor-users${
        supplierId ? `?supplierId=${supplierId}` : ''
      }`,
      responseType: 'blob',
    });
  }

  // export estimate sites
  async exportSites(estimateId) {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: `/export-sites/${estimateId}`,
      responseType: 'blob',
    });
  }

  async exportSitesForDefaultPicture(estimateId, siteIds) {
    this.initAxios();
    return this.axiosCall({
      method: 'POST',
      url: `/export-sites-for-default/${estimateId}`,
      data: { siteIds },
      responseType: 'blob',
    });
  }

  // import estimate sites
  async importSites(file, estimateId) {
    const formData = new FormData();
    formData.append('file', file);
    this.initAxios();
    const headers = LintasAPIService.createHeader(true);
    return this.axiosCall({
      method: 'POST',
      url: `/import-sites/${estimateId}`,
      headers,
      data: formData,
    });
  }

  // import estimate sites default picture
  async importSitesDefaultPicture(file, estimateId) {
    const formData = new FormData();
    formData.append('zipfile', file);
    this.initAxios();
    const headers = LintasAPIService.createHeader(true);
    return this.axiosCall({
      method: 'POST',
      url: `/import-sites-default-pictures/${estimateId}`,
      headers,
      data: formData,
    });
  }

  // export complete details of sites
  async exportCompleteSiteReport(estimateId) {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: `/export-complete-site-reports/${estimateId}`,
      responseType: 'blob',
    });
  }

  // get states
  async getStates() {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: '/get-states',
    });
  }

  // get cities
  async getCities(stateId = 0) {
    this.initAxios();
    let url = '/get-cities';
    if (stateId > 0) {
      url = `/get-cities?stateId=${stateId}`;
    }
    return this.axiosCall({
      method: 'GET',
      url,
    });
  }

  // notify monitor to retake picture
  async notifyMonitor(siteReportId) {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: `/notify-monitor/${siteReportId}`,
    });
  }

  // change start date
  async changeStartDate({ siteId, newStartDate }) {
    this.initAxios();
    return this.axiosCall({
      method: 'POST',
      url: '/change-startdate',
      data: { newStartDate, siteId },
    });
  }

  // import vendor users
  async importVendorUsers(file) {
    const formData = new FormData();
    formData.append('file', file);
    this.initAxios();
    const headers = LintasAPIService.createHeader(true);
    return this.axiosCall({
      method: 'POST',
      url: '/import-monitors',
      headers,
      data: formData,
    });
  }

  // remove mapping
  async removeMapping(siteId) {
    this.initAxios();
    return this.axiosCall({
      method: 'PUT',
      url: `/remove-mapping/${siteId}`,
    });
  }

  // change report date
  async changeReportDate({ siteReportId, newDate }) {
    this.initAxios();
    return this.axiosCall({
      method: 'PUT',
      url: `/change-report-date/${siteReportId}`,
      data: { newDate },
    });
  }

  // remove mapping
  async removeFrequency(siteId) {
    this.initAxios();
    return this.axiosCall({
      method: 'PUT',
      url: `/remove-frequency/${siteId}`,
    });
  }

  // export estimate sites
  async exportSuppliers() {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: '/export-suppliers',
      responseType: 'blob',
    });
  }

  // sync roms data api's
  async syncClients() {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: '/ROMSData/getAllClients',
    });
  }

  async syncProducts() {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: '/ROMSData/getAllProducts',
    });
  }

  async syncSuppliers() {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: '/ROMSData/getAllSuppliers',
    });
  }

  async syncStates() {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: '/ROMSData/getAllStates',
    });
  }

  async syncCities() {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: '/ROMSData/getAllCities',
    });
  }

  // assign flex receive date
  async assignFlexReceiveDate({ siteId, newDate }) {
    this.initAxios();
    return this.axiosCall({
      method: 'POST',
      url: '/assign-flex-receive-date',
      data: { newDate, siteId },
    });
  }

  // get vendor sites
  async getVendorSites({ page, searchQuery }) {
    const url = {
      key: `/vendor-sites?page=${page}`,
    };
    if (searchQuery) {
      const { key } = url;
      const nurl = `${key}&${searchQuery}`;
      url.key = nurl;
    }
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: url.key,
    });
  }

  // update vendor user
  async updateSiteLinkForReport(data, id) {
    this.initAxios();
    return this.axiosCall({
      method: 'PUT',
      url: `/updateSiteLinkForReport/${id}`,
      data,
    });
  }

  // get vendor sites
  async fetchReport({ sd, ed, query }) {
    const url = {
      key: `/report?startDate=${sd}&endDate=${ed}`,
    };
    if (query) {
      const { key } = url;
      const nurl = `${key}&${query}`;
      url.key = nurl;
    }
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: url.key,
      responseType: 'blob',
    });
  }

  // get Ata reports
  async ataFetchReport({ sd, ed, query }) {
    const url = {
      key: `/ata-report?startDate=${sd}&endDate=${ed}`,
    };
    if (query) {
      const { key } = url;
      const nurl = `${key}&${query}`;
      url.key = nurl;
    }
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: url.key,
      responseType: 'blob',
    });
  }

  // getDeletedSitesByEstimateId
  async getDeletedSitesByEstimateId(estimateId) {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: `/ROMSData/getDeletedSitesByEstimateId?estimateId=${estimateId}`,
    });
  }

  // Map deleted site data
  async mapDeletedSiteData(siteId) {
    this.initAxios();
    return this.axiosCall({
      method: 'POST',
      url: '/map-deleted-site',
      data: { siteId },
    });
  }

  async mapEstimateDeletedSiteData(estimateId) {
    this.initAxios();
    return this.axiosCall({
      method: 'PUT',
      url: `/map-estimate-deleted-site/${estimateId}`,
    });
  }

  // get getSuppliersByCompany
  async getSuppliersByCompany(companyCode) {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: `/getSuppliersByCompany?companyCode=${companyCode}`,
    });
  }

  // get getROByClient
  async getROByClient(clientCode) {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: `/getROByClient?clientCode=${clientCode}`,
    });
  }

  // add new task
  async createNewTask(id, taskDate) {
    this.initAxios();
    return this.axiosCall({
      method: 'POST',
      url: '/create-new-task',
      data: { taskDate, id },
    });
  }

  // get all deleted sites
  async getAllDeleteSites({ page, primaryQuery, searchQuery }) {
    const url = {
      key: `/get-deleted-sites?page=${page}`,
    };
    if (primaryQuery) {
      const { key } = url;
      const nurl = `${key}&${primaryQuery}`;
      url.key = nurl;
    }
    if (searchQuery) {
      const { key } = url;
      const nurl = `${key}&${searchQuery}`;
      url.key = nurl;
    }
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: url.key,
    });
  }

  // change end date
  async changeEndDate({ siteId, newEndDate }) {
    this.initAxios();
    return this.axiosCall({
      method: 'POST',
      url: '/change-end-date',
      data: { newEndDate, siteId },
    });
  }

  // get client for client dashboard
  async getClients() {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: '/get-client-user-clients',
    });
  }

  // get Products for client dashboard
  async getProductByClientId(clientId) {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: `/get-client-user-products/${clientId}`,
    });
  }

  // get Campaigns for client dashboard
  async getCampaignsByProductCode(productCode) {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: `/get-client-user-campaigns/${productCode}`,
    });
  }

  // get Campaigns for auditor dashboard
  async getEstimatesByProductCode(productCode) {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: `/get-client-user-estimates/${productCode}`,
    });
  }

  // get Sites for client dashboard
  async getSitesByCampaignNo({ campaignNo, searchQuery }) {
    const url = {
      key: '/get-client-use-sites',
    };
    if (searchQuery) {
      const { key } = url;
      const nurl = `${key}?${searchQuery}`;
      url.key = nurl;
    }
    this.initAxios();
    return this.axiosCall({
      method: 'POST',
      url: url.key,
      data: { campaignNo },
    });
  }

  // get Sites for auditor dashboard
  async getSitesByEstimateNo({ estimateNo, status, searchQuery }) {
    const url = {
      key: '/get-auditor-use-sites',
    };
    if (searchQuery) {
      const { key } = url;
      const nurl = `${key}?${searchQuery}`;
      url.key = nurl;
    }
    this.initAxios();
    return this.axiosCall({
      method: 'POST',
      url: url.key,
      data: { estimateNo, status },
    });
  }

  // get Atr reports
  async atrFetchReport(campaignNo) {
    const url = {
      key: '/ata-client-report',
    };
    this.initAxios();
    return this.axiosCall({
      method: 'POST',
      url: url.key,
      data: campaignNo,
      responseType: 'blob',
    });
  }

  // getClientCampaigns
  async getClientCampaigns() {
    this.initAxios();
    return this.axiosCall({
      method: 'GET',
      url: '/get-client-campaigns',
    });
  }

  // get Atr reports
  async getClientDashboardData({ campaignNo, time }) {
    const url = {
      key: '/get-client-dashboard-data',
    };
    this.initAxios();
    return this.axiosCall({
      method: 'POST',
      url: url.key,
      data: { campaignNo, time },
    });
  }

  async updateSitesAuditorVisibility({
    siteIds, page, primaryQuery, searchQuery,
  }) {
    const url = {
      key: `/updateSitesToVisibleAuditor?page=${page}`,
    };
    if (primaryQuery) {
      const { key } = url;
      const nurl = `${key}&${primaryQuery}`;
      url.key = nurl;
    }
    if (searchQuery) {
      const { key } = url;
      const nurl = `${key}&${searchQuery}`;
      url.key = nurl;
    }
    this.initAxios();
    return this.axiosCall({
      method: 'PUT',
      url: url.key,
      data: { siteIds },
    });
  }

  async updateSiteActionableItem({
    siteId, itemId, action, reason,
  }) {
    const url = {
      key: `/updateSiteActionableItem/${siteId}/${itemId}`,
    };
    this.initAxios();
    return this.axiosCall({
      method: 'PUT',
      url: url.key,
      data: { action, reason },
    });
  }
}

export default LintasAPIService;
